import { formatCurrencyWithRound } from "../../utils";
import { formatDateToStartOfDay } from "../../utils/common";

import {
  BAR_CHART_DEFAULT_COLORS,
  CLASSIFICATION_DISPLAY_NAME,
  FIELD_FORMATTERS,
  SCENARIO_ACTION_DRAWER_TYPE,
  ScenarioActionDrawerType,
} from "./constants";
import {
  BonusForecastChartApi,
  ChartData,
  InOutTreeRow,
  LocCombinedApi,
  ScenarioEdit,
  ScenarioEditType,
  ScenarioLOC,
} from "./types";

export const mapForecastToBarChartSeries = (
  type: string,
  name: string,
  data: BonusForecastChartApi[],
): { series: ChartData; colorsMap?: Record<string, string> } => {
  const barChartData: ChartData = {
    name,
  };
  const colorsMap: Record<string, string> = {};

  if (!data) return { series: barChartData };

  data
    .filter((item: BonusForecastChartApi) => item.chart_type === type)
    .forEach((item, ind) => {
      if (!item.carrier_name) return;
      const value = item.value ?? 0;
      barChartData[item.carrier_name] = value;
      colorsMap[item.carrier_name] =
        BAR_CHART_DEFAULT_COLORS[ind % BAR_CHART_DEFAULT_COLORS.length];
    });

  return { series: barChartData, colorsMap };
};

export function buildTreeRows(data: LocCombinedApi[]): InOutTreeRow[] {
  const groups = new Map<
    string,
    { annualized: number; starting: number; eligible: number }
  >();

  const childRows: InOutTreeRow[] = [];

  for (const row of data) {
    const raw = row.bonus_year_classification ?? "";
    const displayName = CLASSIFICATION_DISPLAY_NAME[raw];
    if (!displayName) continue;

    const childLabel = row.group_name ?? row.group_number ?? "Unknown";

    const annualized = Math.round(row.annualized_premium ?? 0);
    const starting = Math.round(row.starting_premium ?? 0);
    const eligible = Math.round(row.eligible_premium ?? 0);

    const agg = groups.get(displayName) ?? {
      annualized: 0,
      starting: 0,
      eligible: 0,
    };
    agg.annualized += annualized;
    agg.starting += starting;
    agg.eligible += eligible;
    groups.set(displayName, agg);

    childRows.push({
      dataPath: [displayName, childLabel],
      annualized_premium: annualized,
      starting_premium: starting,
      eligible_premium: eligible,
    });
  }

  const parentRows: InOutTreeRow[] = Array.from(groups.entries()).map(
    ([key, agg]) => ({
      dataPath: [key],
      annualized_premium: agg.annualized,
      starting_premium: agg.starting,
      eligible_premium: agg.eligible,
    }),
  );

  return [...parentRows, ...childRows];
}

export const formatNumberValue = (
  key: string,
  value?: string | number | null,
): string => {
  if (value === undefined || value === null) return "-";

  if (typeof value === "number") {
    const formatter = FIELD_FORMATTERS[key];
    switch (formatter?.type) {
      case "percent":
        return `${(value * 100).toFixed(formatter.decimals ?? 0)}%`;
      case "integer":
        return value.toLocaleString();
      default:
        return formatCurrencyWithRound(value);
    }
  }

  return String(value);
};

export function buildScenarioEditBody({
  drawerType,
  selectedRows,
  effectiveDate,
  destinationCarrierId,
}: {
  drawerType: ScenarioActionDrawerType;
  selectedRows: ScenarioLOC[];
  effectiveDate: Date;
  destinationCarrierId?: string;
}): {
  body: Partial<ScenarioEdit>[];
  nullifiedCount: number;
  alreadyTerminatedCount: number;
  alreadyOnCarrierCount: number;
} {
  const isMigrate = drawerType === SCENARIO_ACTION_DRAWER_TYPE.Migrate;
  const effectiveDateStr = formatDateToStartOfDay(effectiveDate);

  let alreadyTerminatedCount = 0;
  let alreadyOnCarrierCount = 0;

  const body = selectedRows
    .filter((row) => {
      const status = (row.status ?? "").trim().toLowerCase();

      // A row can satisfy both conditions (e.g. a terminated row that also
      // happens to sit on the destination carrier). Attribute it to exactly
      // one reason — terminated takes priority — so alreadyTerminatedCount +
      // alreadyOnCarrierCount always equals nullifiedCount; double-counting
      // would inflate the modal beyond how many rows were actually skipped.
      if (status === "terminated") {
        alreadyTerminatedCount += 1;
        return false;
      }
      if (isMigrate && row.carrierId === destinationCarrierId) {
        alreadyOnCarrierCount += 1;
        return false;
      }
      return true;
    })
    .map((row) => ({
      editType: isMigrate
        ? ScenarioEditType.Migrate
        : (drawerType as unknown as ScenarioEditType),
      sourceBaselineLocId: row.id,
      effectiveDate: effectiveDateStr,
      migrateToCarrierId: isMigrate ? destinationCarrierId : undefined,
    }));

  return {
    body,
    nullifiedCount: selectedRows.length - body.length,
    alreadyTerminatedCount,
    alreadyOnCarrierCount,
  };
}
